import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { baseUrl } from "../Univ";
import L from "leaflet";

// Fix default marker issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const pickupIcon = L.divIcon({
  html: `<div class="pickup-icon">📦</div>`,
  className: "",
  iconSize: [30, 30],
});

function DriverDashboard() {
  const [deliveries, setDeliveries] = useState([]);
  const [driverLocation, setDriverLocation] = useState({ lat: 0, lng: 0 });
  const [otpInputs, setOtpInputs] = useState({}); // OTP per delivery

  // ---------------- FETCH DELIVERIES ----------------
  const fetchDeliveries = () => {
    axios
      .get(baseUrl + "driver-deliveries/")
      .then((res) => setDeliveries(res.data))
      .catch((err) => console.log(err));
  };

  // ---------------- UPDATE STATUS ----------------
  const updateStatus = (deliveryId, status) => {
    axios
      .post(baseUrl + "update-delivery-status/", { delivery_id: deliveryId, status })
      .then(() => {
        Swal.fire("Status Updated!", "", "success");
        fetchDeliveries();
      })
      .catch(() => Swal.fire("Error", "Failed to update status", "error"));
  };

  // ---------------- VERIFY OTP ----------------
  const verifyOtp = (deliveryId) => {
    const otp = otpInputs[deliveryId];
    if (!otp) {
      Swal.fire("Error", "Enter OTP from customer", "warning");
      return;
    }

    axios
      .post(baseUrl + "delivery/verify-otp/", { delivery_id: deliveryId, otp })
      .then(() => {
        Swal.fire("Success", "Delivery completed", "success");
        setOtpInputs({ ...otpInputs, [deliveryId]: "" });
        fetchDeliveries();
      })
      .catch((err) =>
        Swal.fire(
          "Error",
          err.response?.data?.error || "Failed to verify OTP",
          "error"
        )
      );
  };

  // ---------------- DRIVER GPS ----------------
  const updateLocation = (lat, lng) => {
    axios
      .post(baseUrl + "update-driver-location/", { lat, lng })
      .catch((err) => console.log(err));
  };

  // ---------------- USE EFFECT ----------------
  useEffect(() => {
    fetchDeliveries();

    const interval = setInterval(() => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((pos) => {
          const { latitude, longitude } = pos.coords;
          setDriverLocation({ lat: latitude, lng: longitude });
          updateLocation(latitude, longitude);
        });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // ---------------- RENDER ----------------
  return (
    <div className="container mt-4">
      <h3>My Deliveries</h3>
      <div className="row">
        {/* Delivery List */}
        <div className="col-md-6">
          <ul className="list-group">
            {deliveries.map((d) => (
              <li key={d.id} className="list-group-item">
                <strong>Order #{d.id}</strong> - {d.order_item_detail} <br />
                Status: <strong>{d.status}</strong>

                <div className="mt-2 d-flex gap-1 flex-column">
                  {d.status === "assigned" && (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => updateStatus(d.id, "picked_up")}
                    >
                      Pick Up
                    </button>
                  )}

                  {d.status === "picked_up" && (
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() => updateStatus(d.id, "shipped")}
                    >
                      Shipped
                    </button>
                  )}

                  {/* OTP verification */}
                  {d.status === "shipped" && (
                    <div className="d-flex gap-1 mt-1">
                      <input
                        type="number"
                        placeholder="Enter OTP from customer"
                        value={otpInputs[d.id] || ""}
                        onChange={(e) =>
                          setOtpInputs({ ...otpInputs, [d.id]: e.target.value })
                        }
                        className="form-control form-control-sm"
                      />
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => verifyOtp(d.id)}
                      >
                        Verify OTP & Deliver
                      </button>
                    </div>
                  )}

                  {d.status === "delivered" && (
                    <span className="text-success mt-1">✔ Delivered</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Map */}
        <div className="col-md-6">
          <h5>My Current Location</h5>
          {driverLocation.lat !== 0 ? (
            <MapContainer
              center={[driverLocation.lat, driverLocation.lng]}
              zoom={13}
              style={{ height: "400px", width: "100%" }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={[driverLocation.lat, driverLocation.lng]}>
                <Popup>You are here</Popup>
              </Marker>

              {deliveries
                .filter((d) => d.delivery_lat && d.delivery_lng)
                .map((d) => (
                  <Marker
                    key={d.id}
                    position={[d.delivery_lat, d.delivery_lng]}
                    icon={new L.Icon({
                      iconUrl: require("leaflet/dist/images/marker-icon.png"),
                      iconSize: [25, 41],
                    })}
                  >
                    <Popup>
                      Delivery #{d.id} - {d.order_item_detail}
                    </Popup>
                  </Marker>
                ))}

              {deliveries
                .filter((d) => d.current_lat && d.current_lng)
                .map((d) => (
                  <Marker
                    key={"pickup-" + d.id}
                    position={[d.current_lat, d.current_lng]}
                    icon={pickupIcon}
                  >
                    <Popup>
                      📦 Pickup Location
                      <br />
                      Order #{d.id}
                    </Popup>
                  </Marker>
                ))}
            </MapContainer>
          ) : (
            <p>Fetching location...</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default DriverDashboard;
